import React, { useState } from "react";

const DEFAULT_EXCLUDED_SELECTORS = [
  "[data-download-exclude]",
  ".reference-check-signature-watermark",
  "button",
  "input[type='file']",
];

const resolveTarget = (targetRef, targetSelector) =>
  targetRef?.current ||
  (targetSelector ? document.querySelector(targetSelector) : null);

const normalizeFilename = (filename, format) => {
  const extension = format === "png" ? "png" : "pdf";
  const safeName = String(filename || "document")
    .trim()
    .replace(/[<>:"/\\|?*]/g, "-")
    .replace(/\s+/g, "-")
    .replace(/\.(png|pdf)$/i, "");

  return `${safeName || "document"}.${extension}`;
};

const canvasToBlob = (canvas) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (result) =>
        result ? resolve(result) : reject(new Error("Unable to create the file.")),
      "image/png",
    );
  });

const downloadBlob = (blob, filename) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

const fitPreservedMedia = (root, frameOptions) => {
  if (!frameOptions) return;

  const frameWidth = Number(frameOptions.width);
  const frameHeight = Number(frameOptions.height);
  const framePadding = Math.max(0, Number(frameOptions.padding) || 0);
  if (!frameWidth || !frameHeight) return;

  root.querySelectorAll("[data-download-media-frame]").forEach((frame) => {
    const media = frame.querySelector("[data-download-preserve-ratio]");
    if (!media) return;

    const sourceWidth =
      Number(media.naturalWidth) || Number(media.width) || media.clientWidth;
    const sourceHeight =
      Number(media.naturalHeight) || Number(media.height) || media.clientHeight;
    if (!sourceWidth || !sourceHeight) return;

    const availableWidth = Math.max(1, frameWidth - framePadding * 2);
    const availableHeight = Math.max(1, frameHeight - framePadding * 2);
    const fitScale = Math.min(
      availableWidth / sourceWidth,
      availableHeight / sourceHeight,
    );
    const renderedWidth = sourceWidth * fitScale;
    const renderedHeight = sourceHeight * fitScale;

    Object.assign(frame.style, {
      width: `${frameWidth}px`,
      height: `${frameHeight}px`,
      maxWidth: "100%",
      padding: `${framePadding}px`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
      boxSizing: "border-box",
      background: "#ffffff",
      border: "1px solid #94a3b8",
      borderRadius: "4px",
      boxShadow: "none",
    });

    media.style.setProperty("width", `${renderedWidth}px`, "important");
    media.style.setProperty("height", `${renderedHeight}px`, "important");
    media.style.setProperty("max-width", "none", "important");
    media.style.setProperty("padding", "0", "important");
    media.style.setProperty("margin", "0", "important");
    media.style.setProperty("border", "0", "important");
    media.style.setProperty("border-radius", "0", "important");
    media.style.setProperty("box-shadow", "none", "important");
    media.style.setProperty("object-fit", "fill", "important");
    media.style.setProperty("transform", "none", "important");
  });
};

const renderEmbeddedPdfFrames = async (target, documentWidth) => {
  const frames = Array.from(
    target.querySelectorAll("iframe[data-document-pdf], iframe[title='Task PDF']"),
  ).filter((frame) => frame.src);
  if (!frames.length) return () => {};

  const pdfjsModule = await import("pdfjs-dist/legacy/build/pdf");
  const getDocument =
    pdfjsModule.getDocument || pdfjsModule.default?.getDocument;
  const preparedFrames = [];

  for (const frame of frames) {
    try {
      const response = await fetch(frame.src);
      if (!response.ok) throw new Error(`PDF request failed (${response.status}).`);

      const pdf = await getDocument({
        data: await response.arrayBuffer(),
        disableWorker: true,
      }).promise;
      const pages = document.createElement("div");
      pages.setAttribute("data-rendered-document-pdf", "");
      Object.assign(pages.style, {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "16px",
        background: "#ffffff",
      });

      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
        const page = await pdf.getPage(pageNumber);
        const baseViewport = page.getViewport({ scale: 1 });
        const availableWidth = Math.max(1, documentWidth - 80);
        const renderScale = Math.min(2, availableWidth / baseViewport.width);
        const viewport = page.getViewport({ scale: renderScale });
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = Math.ceil(viewport.width);
        pageCanvas.height = Math.ceil(viewport.height);
        Object.assign(pageCanvas.style, {
          display: "block",
          width: `${Math.min(availableWidth, viewport.width)}px`,
          maxWidth: "100%",
          height: "auto",
          background: "#ffffff",
          boxShadow: "0 0 0 1px #d1d5db",
        });
        await page.render({
          canvasContext: pageCanvas.getContext("2d"),
          viewport,
        }).promise;
        pages.appendChild(pageCanvas);
      }

      frame.parentNode?.insertBefore(pages, frame);
      const previousDisplay = frame.style.display;
      frame.style.display = "none";
      preparedFrames.push({ frame, pages, previousDisplay });
    } catch (error) {
      console.error("Embedded task PDF preparation failed:", error);
    }
  }

  return () => {
    preparedFrames.forEach(({ frame, pages, previousDisplay }) => {
      pages.remove();
      frame.style.display = previousDisplay;
    });
  };
};

const canvasToPdfBlob = async ({
  canvas,
  filename,
  pageFormat,
  orientation,
  marginMm,
  footerText,
  showPageNumbers,
  avoidBreakRanges = [],
}) => {
  const { jsPDF } = await import("jspdf");
  const pdf = new jsPDF({
    orientation,
    unit: "mm",
    format: pageFormat,
    compress: true,
  });
  pdf.setProperties({ title: filename.replace(/\.pdf$/i, "") });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentWidth = pageWidth - marginMm * 2;
  const contentHeight = pageHeight - marginMm * 2;
  const pageHeightPx = Math.max(
    1,
    Math.floor((canvas.width * contentHeight) / contentWidth),
  );
  const pageSlices = [];
  let sourceY = 0;

  while (sourceY < canvas.height) {
    const remainingHeight = canvas.height - sourceY;
    let sliceHeight = Math.min(pageHeightPx, remainingHeight);

    if (remainingHeight > pageHeightPx) {
      const proposedBreak = sourceY + pageHeightPx;
      const containingRange = avoidBreakRanges
        .filter(
          ({ top, bottom }) =>
            top < proposedBreak &&
            bottom > proposedBreak &&
            bottom - top < pageHeightPx * 0.85,
        )
        .sort(
          (first, second) =>
            first.bottom - first.top - (second.bottom - second.top),
        )[0];
      const safeBreak = Math.floor(containingRange?.top || proposedBreak);

      if (safeBreak - sourceY >= pageHeightPx * 0.45) {
        sliceHeight = safeBreak - sourceY;
      }
    }

    pageSlices.push({ sourceY, sliceHeight });
    sourceY += sliceHeight;
  }

  const totalPages = pageSlices.length;

  let pageIndex = 0;
  for (const pageSlice of pageSlices) {
    const { sourceY: pageSourceY, sliceHeight } = pageSlice;
    const pageCanvas = document.createElement("canvas");
    pageCanvas.width = canvas.width;
    pageCanvas.height = sliceHeight;
    const context = pageCanvas.getContext("2d");
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
    context.drawImage(
      canvas,
      0,
      pageSourceY,
      canvas.width,
      sliceHeight,
      0,
      0,
      canvas.width,
      sliceHeight,
    );

    if (pageIndex > 0) pdf.addPage(pageFormat, orientation);
    const renderedHeight = (sliceHeight * contentWidth) / canvas.width;
    pdf.addImage(
      pageCanvas.toDataURL("image/png"),
      "PNG",
      marginMm,
      marginMm,
      contentWidth,
      renderedHeight,
      undefined,
      "FAST",
    );
    const currentPage = pageIndex + 1;
    if (footerText || showPageNumbers) {
      const footerLineY = pageHeight - marginMm + 2;
      const footerTextY = footerLineY + 4;
      pdf.setDrawColor(203, 213, 225);
      pdf.setLineWidth(0.2);
      pdf.line(marginMm, footerLineY, pageWidth - marginMm, footerLineY);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(100, 116, 139);
      if (footerText) pdf.text(footerText, marginMm, footerTextY);
      if (showPageNumbers) {
        pdf.text(
          `Page ${currentPage} of ${totalPages}`,
          pageWidth - marginMm,
          footerTextY,
          { align: "right" },
        );
      }
    }
    pageIndex += 1;
  }

  return pdf.output("blob");
};

export const createDocumentFile = async ({
  targetRef,
  targetSelector,
  target: suppliedTarget,
  filename = "document",
  format = "pdf",
  pageFormat = "a4",
  orientation = "portrait",
  marginMm = 12,
  footerText = "",
  showPageNumbers = true,
  captureWidth,
  capturePaddingPx = 40,
  preservedMediaFrame,
  scale = 2,
  backgroundColor = "#ffffff",
  excludeSelectors = DEFAULT_EXCLUDED_SELECTORS,
  onClone,
}) => {
  const target = suppliedTarget || resolveTarget(targetRef, targetSelector);
  if (!target) {
    throw new Error("The document area is not available.");
  }

  await document.fonts?.ready;
  const { default: html2canvas } = await import("html2canvas");
  const outputFormat = String(format).toLowerCase() === "png" ? "png" : "pdf";
  const documentWidth =
    Number(captureWidth) || (orientation === "landscape" ? 1123 : 794);
  const selectors = Array.isArray(excludeSelectors)
    ? excludeSelectors.filter(Boolean)
    : [];

  const cleanupEmbeddedPdfs = await renderEmbeddedPdfFrames(
    target,
    documentWidth,
  );
  let canvas;
  let clonedDocumentHeight = 0;
  let avoidBreakRanges = [];
  try {
    canvas = await html2canvas(target, {
      scale,
      backgroundColor,
      useCORS: true,
      logging: false,
      windowWidth: documentWidth,
      ignoreElements: (element) =>
        selectors.some((selector) => element.matches?.(selector)),
      onclone: (clonedDocument, clonedTarget) => {
        clonedTarget.classList.remove("slide-narrow");
        Object.assign(clonedTarget.style, {
          width: `${documentWidth}px`,
          maxWidth: "none",
          minWidth: "0",
          height: "auto",
          minHeight: "0",
          margin: "0",
          padding: `${capturePaddingPx}px`,
          boxSizing: "border-box",
          overflow: "visible",
        });
        clonedTarget.style.setProperty(
          "background",
          backgroundColor,
          "important",
        );
        clonedTarget.style.setProperty(
          "background-color",
          backgroundColor,
          "important",
        );
        clonedTarget.style.setProperty(
          "padding",
          `${capturePaddingPx}px`,
          "important",
        );
        clonedTarget.style.setProperty("border-radius", "0", "important");
        clonedTarget.style.setProperty("box-shadow", "none", "important");
        fitPreservedMedia(clonedTarget, preservedMediaFrame);
        onClone?.(clonedDocument, clonedTarget);

        const targetRect = clonedTarget.getBoundingClientRect();
        clonedDocumentHeight = Math.max(
          clonedTarget.scrollHeight,
          targetRect.height,
          1,
        );
        const breakSelectors = [
          "h1",
          "h2",
          "h3",
          "h4",
          "h5",
          "h6",
          "p",
          "li",
          "tr",
          "fieldset",
          "[data-download-media-frame]",
          "[data-rendered-document-pdf] > canvas",
        ].join(",");
        avoidBreakRanges = Array.from(
          clonedTarget.querySelectorAll(breakSelectors),
        )
          .filter((element) => {
            const style = clonedDocument.defaultView?.getComputedStyle(element);
            const rect = element.getBoundingClientRect();
            return (
              style?.display !== "none" &&
              style?.visibility !== "hidden" &&
              rect.height > 0
            );
          })
          .map((element) => {
            const rect = element.getBoundingClientRect();
            return {
              top: Math.max(0, rect.top - targetRect.top),
              bottom: Math.max(0, rect.bottom - targetRect.top),
            };
          });
      },
    });
  } finally {
    cleanupEmbeddedPdfs();
  }

  const outputFilename = normalizeFilename(filename, outputFormat);
  const verticalScale = canvas.height / Math.max(clonedDocumentHeight, 1);
  const scaledAvoidBreakRanges = avoidBreakRanges.map(({ top, bottom }) => ({
    top: top * verticalScale,
    bottom: bottom * verticalScale,
  }));
  const blob =
    outputFormat === "png"
      ? await canvasToBlob(canvas)
      : await canvasToPdfBlob({
          canvas,
          filename: outputFilename,
          pageFormat,
          orientation,
          marginMm,
          footerText,
          showPageNumbers,
          avoidBreakRanges: scaledAvoidBreakRanges,
        });

  return { filename: outputFilename, blob, format: outputFormat };
};

const DocumentDownloadButton = ({
  targetRef,
  targetSelector,
  filename = "document",
  label = "Download",
  className = "btn btn-outline-primary",
  format = "pdf",
  pageFormat = "a4",
  orientation = "portrait",
  marginMm = 12,
  footerText = "",
  showPageNumbers = true,
  captureWidth,
  capturePaddingPx = 40,
  preservedMediaFrame,
  scale = 2,
  backgroundColor = "#ffffff",
  excludeSelectors = DEFAULT_EXCLUDED_SELECTORS,
  disabled = false,
  onBeforeDownload,
  onClone,
  onDownloaded,
  onError,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    if (disabled || isDownloading) return;

    const target = resolveTarget(targetRef, targetSelector);
    if (!target) {
      const error = new Error("The document area is not available.");
      onError?.(error);
      if (!onError) window.alert(error.message);
      return;
    }

    try {
      setIsDownloading(true);
      await onBeforeDownload?.();
      const result = await createDocumentFile({
        target,
        filename,
        format,
        pageFormat,
        orientation,
        marginMm,
        footerText,
        showPageNumbers,
        captureWidth,
        capturePaddingPx,
        preservedMediaFrame,
        scale,
        backgroundColor,
        excludeSelectors,
        onClone,
      });

      downloadBlob(result.blob, result.filename);
      onDownloaded?.(result);
    } catch (error) {
      console.error("Document download failed:", error);
      onError?.(error);
      if (!onError) window.alert("Failed to download the document.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <button
      type="button"
      className={className}
      onClick={handleDownload}
      disabled={disabled || isDownloading}
    >
      {isDownloading ? "Preparing PDF..." : label}
    </button>
  );
};

export default DocumentDownloadButton;
